import React, { useState } from 'react';
import axiosInstance from '@/lib/axios';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyForm({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    postal_code: '',
    surface_m2: '',
    nb_rooms: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo trop lourde (max 5MB)');
        continue;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoUrls(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (index) => {
    setPhotoUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.address || !formData.postal_code || !formData.surface_m2 || !formData.nb_rooms) {
      toast.error('Remplis tous les champs obligatoires !');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        address: formData.address,
        postal_code: formData.postal_code,
        surface_m2: parseFloat(formData.surface_m2),
        nb_rooms: parseInt(formData.nb_rooms),
        photo_urls: photoUrls
      };

      const response = await axiosInstance.post(`${API}/properties`, payload, {
        withCredentials: true
      });

      toast.success('🏠 Bien créé avec succès ! 🌟');
      navigate(`/properties/${response.data.property_id}`);
    } catch (error) {
      console.error('Error creating property:', error);
      toast.error('Erreur lors de la création du bien');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030305] relative">
      <div className="cyber-grid-bg"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/20">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/properties')}
                data-testid="back-button"
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Mes biens
              </Button>
              <h1 className="text-xl font-bold font-['Orbitron'] tracking-wider text-white">
                NOUVEAU <span className="text-cyan-400">BIEN</span>
              </h1>
              <div className="w-32"></div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-6 py-12 max-w-3xl">
          {/* Guide */}
          <div className="glass-panel p-6 rounded-lg mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                📝
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-cyan-400 mb-2">
                  Décrivez votre projet immobilier
                </h3>
                <p className="text-slate-300 text-sm">
                  Enregistrez les infos de base de votre bien. Vous pourrez ensuite ajouter toutes vos dépenses au fil de l'eau 
                  et créer des simulations de rentabilité. 💎
                </p>
              </div>
            </div>
          </div>

          <div className="glass-panel p-8 rounded-lg space-y-6">
            {/* Nom du bien */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🏠</span>
                <Label htmlFor="name" className="text-slate-300 font-semibold">
                  Comment s'appelle ce bien ?
                </Label>
              </div>
              <Input
                id="name"
                data-testid="property-name-input"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ex: Appartement Lyon 6ème, Maison Bordeaux..."
                className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white"
              />
            </div>

            {/* Adresse */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">📍</span>
                <Label htmlFor="address" className="text-slate-300 font-semibold">
                  Où se trouve ce trésor ?
                </Label>
              </div>
              <Input
                id="address"
                data-testid="property-address-input"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="12 rue de la République, Lyon"
                className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white"
              />
            </div>

            {/* Code postal */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔢</span>
                <Label htmlFor="postal_code" className="text-slate-300 font-semibold">
                  Code postal ?
                </Label>
              </div>
              <Input
                id="postal_code"
                data-testid="property-postal-input"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="69006"
                className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                💡 Permet d'estimer précisément les coûts d'eau
              </p>
            </div>

            {/* Surface et pièces */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">📐</span>
                  <Label htmlFor="surface_m2" className="text-slate-300 font-semibold">
                    Combien de m² ?
                  </Label>
                </div>
                <Input
                  id="surface_m2"
                  data-testid="property-surface-input"
                  type="number"
                  value={formData.surface_m2}
                  onChange={(e) => handleChange('surface_m2', e.target.value)}
                  placeholder="75"
                  className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🚪</span>
                  <Label htmlFor="nb_rooms" className="text-slate-300 font-semibold">
                    Combien de pièces ?
                  </Label>
                </div>
                <Input
                  id="nb_rooms"
                  data-testid="property-rooms-input"
                  type="number"
                  value={formData.nb_rooms}
                  onChange={(e) => handleChange('nb_rooms', e.target.value)}
                  placeholder="3"
                  className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">📝</span>
                <Label htmlFor="description" className="text-slate-300 font-semibold">
                  Une petite description ? (optionnel)
                </Label>
              </div>
              <Textarea
                id="description"
                data-testid="property-description-input"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="T3 rénové, balcon, proche métro..."
                className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white min-h-[100px]"
              />
            </div>

            {/* Photos */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">📸</span>
                <Label className="text-slate-300 font-semibold">
                  Ajoute quelques photos pour visualiser ton investissement !
                </Label>
              </div>
              
              {/* Photo Grid */}
              {photoUrls.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mb-4">
                  {photoUrls.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-cyan-500/30"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-cyan-500/30 rounded-lg cursor-pointer hover:border-cyan-500/50 transition-colors bg-cyan-500/5">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-cyan-400" />
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold">Clique pour uploader</span> ou glisse tes photos
                  </p>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG jusqu'à 5MB</p>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="photo-upload-input"
                />
              </label>
            </div>

            {/* Submit */}
            <div className="pt-6">
              <Button
                onClick={handleSubmit}
                data-testid="submit-property-button"
                disabled={loading}
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider text-lg py-6"
              >
                {loading ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-black border-t-transparent mr-2"></div>
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Créer mon bien
                  </>
                )}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
